import { PlayArrow } from "@mui/icons-material"
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { makeStyles } from "tss-react/mui-compat"
import { SHOW_VIDEO_COUNT } from "app-constants/constants"
import AspectRatio from "components/common/AspectRatio"
import Link, { LinkProps } from "next/link"
import pluralize from "pluralize"
import React from "react"

interface FeaturedVideoCardProps {
  onPlay: () => void
  href: LinkProps["href"]
  imageSrc: string
  imageAlt: string
  curatorName: string
  title: string
  numberOfViews: number
}

const useStyles = makeStyles()(
  (theme) => ({
    card: {
      display: "flex",
      flexFlow: "column nowrap",
      width: "100%",
      minWidth: 280,
      maxWidth: 504,
      backgroundColor: "inherit",
    },
    cardMediaContainer: {
      position: "relative",
      width: "100%",
    },
    cardMediaPlayButton: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      fontSize: 36,
      color: "white",
      backgroundImage: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${alpha(
        theme.palette.primary.main,
        0.7,
      )})`,
      boxShadow: "0px 0px 2px rgba(0, 0, 0, 0.1), 0px 2px 2px rgba(0, 0, 0, 0.25)",

      "&:hover": {
        backgroundImage: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${alpha(
          theme.palette.primary.main,
          0.9,
        )})`,
      },
    },
    cardActionArea: {
      flex: 1,
    },
    curatorName: {
      ...theme.fonts.lato400,
      fontSize: 16,
    },
    title: {
      ...theme.fonts.raleway600,
      fontSize: 24,
    },
    numberOfViews: {
      ...theme.fonts.lato400,
      fontSize: 14,
    },
  }),
)

const FeaturedVideoCard = (props: FeaturedVideoCardProps) => {
  const { onPlay, imageSrc, href, curatorName, title, numberOfViews, imageAlt } = props
  const { classes } = useStyles()

  const handlePlay = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    onPlay()
  }

  return (
    <Card elevation={0} raised={false} className={classes.card}>
      <Box className={classes.cardMediaContainer}>
        <AspectRatio ratio={10 / 6}>
          <CardMedia
            aria-hidden={imageAlt.length === 0}
            component="img"
            image={imageSrc}
            alt={imageAlt}
          />
        </AspectRatio>
        <IconButton
          color="primary"
          aria-label={`Play ${title} video`}
          className={classes.cardMediaPlayButton}
          onClick={handlePlay}
          size="large">
          <PlayArrow fontSize="inherit" color="inherit" />
        </IconButton>
      </Box>
      <Link href={href} passHref legacyBehavior>
        <CardActionArea className={classes.cardActionArea}>
          <CardContent>
            <Typography gutterBottom variant="body2" className={classes.curatorName}>
              {curatorName}
            </Typography>
            <Typography gutterBottom variant="h2" className={classes.title}>
              {title}
            </Typography>
            {SHOW_VIDEO_COUNT && (
              <Typography gutterBottom variant="body2" className={classes.numberOfViews}>
                {pluralize(`${numberOfViews.toLocaleString()} views`)}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Link>
    </Card>
  )
}

export default FeaturedVideoCard
